import { useMemo, useState } from "react";

interface AprCalculatorProps {
  variant: "rate" | "fixed";
  ctaHref: string;
}

const FIXED_ANNUAL_RATE = 35;
const GST_RATE = 0.18;

const formatAmount = (value: number) => `₹${value.toFixed(2)}`;

interface SliderProps {
  label: string;
  display: string;
  min: number;
  max: number;
  step: number;
  value: number;
  minLabel: string;
  maxLabel: string;
  onChange: (value: number) => void;
}

const Slider = ({ label, display, min, max, step, value, minLabel, maxLabel, onChange }: SliderProps) => (
  <div>
    <div className="flex items-center justify-between gap-4 text-[14px] font-bold text-[#080808]">
      <span>{label}</span>
      <span className="whitespace-nowrap">{display}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      aria-label={label}
      onChange={(e) => onChange(Number(e.target.value))}
      className="apr-slider mt-[13px] h-[6px] w-full cursor-pointer appearance-none rounded-[3px] bg-[#e5ebf8] outline-none"
    />
    <div className="flex justify-between pt-[10px] text-[12px] leading-[14px] text-[#8491a5]">
      <span>{minLabel}</span>
      <span>{maxLabel}</span>
    </div>
  </div>
);

const AprCalculator = ({ variant, ctaHref }: AprCalculatorProps) => {
  const [amount, setAmount] = useState(50000);
  const [days, setDays] = useState(variant === "rate" ? 365 : 180);
  const [rate, setRate] = useState(36);
  const [fee, setFee] = useState(5);

  const annualRate = variant === "rate" ? rate : FIXED_ANNUAL_RATE;

  const breakup = useMemo(() => {
    const interest = (amount * annualRate * days) / (100 * 365);
    const feeAmount = (amount * fee) / 100;
    const gst = feeAmount * GST_RATE;
    const net = amount - feeAmount - gst;
    const apr = ((interest + feeAmount + gst) / net) * (365 / days) * 100;
    return { interest, feeAmount, gst, net, total: amount + interest, apr };
  }, [amount, annualRate, days, fee]);

  const rows = [
    { label: "Interest Amount", value: breakup.interest },
    { label: "Processing Fee", value: breakup.feeAmount },
  ];

  return (
    <div className="mx-auto grid w-full max-w-[1081px] grid-cols-1 gap-6 lg:grid-cols-[538fr_489fr] lg:gap-9">
      <div className="flex flex-col gap-6 rounded-[10px] bg-white px-5 py-7 sm:px-[29px] sm:py-[33px] lg:rounded-none">
        <div className="flex flex-col gap-2 pt-[2px]">
          <p className="text-[18px] font-bold leading-[24px] text-[#048be1]">Enter your loan details</p>
          <p className="text-[12px] leading-[15px] text-[#8491a5]">All figures update instantly as you adjust the sliders.</p>
        </div>

        <Slider
          label="Loan Amount"
          display={`₹${amount}`}
          min={5000}
          max={100000}
          step={1000}
          value={amount}
          minLabel="₹5,000"
          maxLabel="₹1,00,000"
          onChange={setAmount}
        />
        <Slider
          label={variant === "rate" ? "Tenure (Days)" : "Repayment Period"}
          display={`${days} ${days === 1 ? "Day" : "Days"}`}
          min={1}
          max={365}
          step={1}
          value={days}
          minLabel="1 Day"
          maxLabel="365 Days"
          onChange={setDays}
        />
        {variant === "rate" && (
          <Slider
            label="Rate of Interest (% per annum)"
            display={`${rate}% p.a.`}
            min={15}
            max={36}
            step={1}
            value={rate}
            minLabel="15% p.a."
            maxLabel="36% p.a."
            onChange={setRate}
          />
        )}
        <Slider
          label="Processing Fee (%)"
          display={`${fee}%`}
          min={5}
          max={10}
          step={0.5}
          value={fee}
          minLabel="5%"
          maxLabel="10%"
          onChange={setFee}
        />
        {variant === "fixed" && (
          <div>
            <div className="flex items-center justify-between gap-4 text-[14px] font-bold text-[#080808]">
              <span>Monthly Interest Rate</span>
              <span>{(FIXED_ANNUAL_RATE / 12).toFixed(4)}%</span>
            </div>
            <div className="flex justify-between pt-[10px] text-[12px] leading-[14px] text-[#8491a5]">
              <span>Annualised equivalent</span>
              <span>{FIXED_ANNUAL_RATE}% p.a.</span>
            </div>
          </div>
        )}

        <div className="rounded-[7px] bg-[#eef2ff] px-[14px] py-3">
          <p className="text-[11.3px] leading-[19px] text-[#263f66]">
            {variant === "rate"
              ? "GST at 18% is applied on the processing fee only, as per government norms, and is already included in the calculation."
              : "GST at 18% applies to the processing fee and is included in the estimated cost calculation."}
          </p>
        </div>
      </div>

      <div className="flex flex-col rounded-[10px] bg-white p-5 sm:p-[27px]">
        <p className="text-[15px] font-bold leading-[22px] text-[#080808]">Your loan breakup</p>
        <div className="flex flex-col pt-[19px]">
          {rows.map((row) => (
            <div key={row.label} className="flex h-[41px] items-center justify-between border-b border-[#e0e6f2] text-[#52678e]">
              <span className="text-[13px]">{row.label}</span>
              <span className="text-[14px]">{formatAmount(row.value)}</span>
            </div>
          ))}
          <div className="flex h-[38px] items-center justify-between border-b border-[#e0e6f2] pl-[13px] text-[#8491a5]">
            <span className="text-[11px]">+ GST (18% on fee)</span>
            <span className="text-[12px]">{formatAmount(breakup.gst)}</span>
          </div>
          <div className="flex h-[41px] items-center justify-between border-b border-[#e0e6f2] text-[#52678e]">
            <span className="text-[13px]">Net Disbursed Amount</span>
            <span className="text-[14px]">{formatAmount(breakup.net)}</span>
          </div>
          <div className="flex h-[43px] items-center justify-between border-b border-[#e0e6f2] font-bold text-[#080808]">
            <span className="text-[13px]">Total Repayment</span>
            <span className="text-[14px]">{formatAmount(breakup.total)}</span>
          </div>
        </div>

        <div className="pt-[18px]">
          <div className="flex flex-col gap-2 rounded-[7px] bg-[#edf2fd] px-[18px] py-[17px] font-bold text-[#048be1]">
            <p className="text-[12px] leading-[17px]">ESTIMATED APR</p>
            <p className="text-[28px] leading-[35px] sm:text-[32px]">
              {variant === "rate" ? `${breakup.apr.toFixed(2)}%` : `Up to ${FIXED_ANNUAL_RATE}%`}
            </p>
            {variant === "fixed" && (
              <p className="text-[11.3px] font-normal leading-[18px] text-[#233958]">
                Your actual APR will depend on the loan amount, tenure, interest, fees, and applicable charges.
              </p>
            )}
          </div>
        </div>

        <div className="pb-[17px] pt-[18px]">
          <div className="rounded-[7px] border border-[#ffd18b] bg-[#edf2fd] px-4 py-[14px]">
            <p className="text-[11.3px] leading-[18px] text-[#233958]">
              <span className="font-bold text-[#080808]">Indicative Estimate Only:</span>
              {variant === "rate" ? (
                <>
                  {" "}The figures shown are for illustrative purposes only. Actual interest rates, processing fees, APR, and repayment amounts are determined by your lending partner and will be disclosed in the{" "}
                  <span className="font-bold text-[#080808]">Key Fact Statement (KFS)</span>
                  {" "}before loan acceptance, as required under RBI guidelines. Actual figures may vary based on your credit profile and lender assessment.
                </>
              ) : (
                " Actual loan terms, interest rates, fees, APR, and repayment amounts will be disclosed in your Key Fact Statement (KFS) before you accept the loan."
              )}
            </p>
          </div>
        </div>

        <a
          href={ctaHref}
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-[50px] w-full items-center justify-center rounded-[5px] bg-[#048be1] px-8 text-center text-[16px] font-bold leading-[24px] text-white transition-colors hover:bg-[#0379c4]"
        >
          Apply For A Personal Loan
        </a>
      </div>

      <style>{`
        .apr-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #048be1;
          border: 3px solid #ffffff;
          box-shadow: 0 0 0 1px #048be1;
          cursor: pointer;
        }
        .apr-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #048be1;
          border: 3px solid #ffffff;
          box-shadow: 0 0 0 1px #048be1;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default AprCalculator;
